"use client";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from "react";
const CIRCLE_SIZE = 220;
const STROKE_WIDTH = 14;
const CIRCLE_RADIUS = (CIRCLE_SIZE - STROKE_WIDTH) / 2;
const CIRCUMFERENCE = 2 * Math.PI * CIRCLE_RADIUS;
// Switch from text1 to text2 at the 5th second of the countdown, with a quick fade.
const TEXT_SWITCH_AT_MS = 5000;
const TEXT_FADE_DURATION_MS = 400;
function formatRemainingTime(remainingMs) {
    const totalSeconds = Math.max(0, Math.ceil(remainingMs / 1000));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}
export function Countdown({ duration, text1, text2, primaryColor, secondaryColor, backgroundColor = "#ffffff", fullScreen = false, className, }) {
    const safeDurationSeconds = Math.max(0, duration);
    const totalMs = safeDurationSeconds * 1000;
    const [remainingMs, setRemainingMs] = useState(totalMs);
    const progressRatio = totalMs > 0 ? remainingMs / totalMs : 0;
    const progressOffset = CIRCUMFERENCE * (1 - progressRatio);
    const elapsedMs = Math.max(0, totalMs - remainingMs);
    const fadeProgress = Math.min(1, Math.max(0, (elapsedMs - TEXT_SWITCH_AT_MS) / TEXT_FADE_DURATION_MS));
    const countdownText = useMemo(() => formatRemainingTime(remainingMs), [remainingMs]);
    const containerStyle = fullScreen
        ? {
            position: "fixed",
            inset: 0,
            zIndex: 9999,
            background: backgroundColor,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "24px",
        }
        : undefined;
    useEffect(() => {
        setRemainingMs(totalMs);
        if (totalMs <= 0) {
            return;
        }
        const startedAt = performance.now();
        let frame = 0;
        const animate = (now) => {
            const elapsedMs = now - startedAt;
            const nextRemainingMs = Math.max(totalMs - elapsedMs, 0);
            setRemainingMs(nextRemainingMs);
            if (nextRemainingMs > 0) {
                frame = window.requestAnimationFrame(animate);
            }
        };
        frame = window.requestAnimationFrame(animate);
        return () => {
            window.cancelAnimationFrame(frame);
        };
    }, [totalMs]);
    return (_jsx("div", { className: className, style: containerStyle, children: _jsxs("div", { style: {
                width: "100%",
                maxWidth: "520px",
                minHeight: "500px",
                border: `1px solid ${primaryColor}`,
                borderRadius: "20px",
                backgroundColor,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "24px",
                padding: "40px 32px 36px",
                textAlign: "center",
            }, children: [_jsxs("div", { style: { position: "relative", minHeight: "132px", width: "100%" }, children: [_jsx("div", { "aria-hidden": fadeProgress >= 1, style: {
                                opacity: 1 - fadeProgress,
                                transition: "opacity 200ms ease",
                                fontSize: "24px",
                                fontWeight: "500",
                                lineHeight: "1.25",
                                letterSpacing: "-0.01em",
                            }, children: text1 }), _jsx("div", { "aria-hidden": fadeProgress <= 0, style: {
                                position: "absolute",
                                inset: 0,
                                opacity: fadeProgress,
                                transition: "opacity 200ms ease",
                                fontSize: "24px",
                                fontWeight: "500",
                                lineHeight: "1.25",
                                letterSpacing: "-0.01em",
                            }, children: text2 })] }), _jsxs("div", { style: {
                        position: "relative",
                        width: `${CIRCLE_SIZE}px`,
                        height: `${CIRCLE_SIZE}px`,
                    }, children: [_jsxs("svg", { width: CIRCLE_SIZE, height: CIRCLE_SIZE, viewBox: `0 0 ${CIRCLE_SIZE} ${CIRCLE_SIZE}`, children: [_jsx("circle", { cx: CIRCLE_SIZE / 2, cy: CIRCLE_SIZE / 2, r: CIRCLE_RADIUS, stroke: secondaryColor, strokeWidth: STROKE_WIDTH, fill: "none" }), _jsx("circle", { cx: CIRCLE_SIZE / 2, cy: CIRCLE_SIZE / 2, r: CIRCLE_RADIUS, stroke: primaryColor, strokeWidth: STROKE_WIDTH, strokeLinecap: "round", fill: "none", strokeDasharray: CIRCUMFERENCE, strokeDashoffset: progressOffset, transform: `rotate(-90 ${CIRCLE_SIZE / 2} ${CIRCLE_SIZE / 2})` })] }), _jsx("div", { style: {
                                position: "absolute",
                                inset: 0,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "56px",
                                fontWeight: "700",
                                lineHeight: "1",
                                color: "#000000",
                            }, children: countdownText })] })] }) }));
}
